/**
 * Built-in validation keyword registry.
 *
 * Maps custom domain.json property keywords (noBlank, noDoubleSpace, digitsOnly,
 * charset, exactLength) to regex + message pairs. The FormLoader reads these
 * keywords flat on the property object — same level as minLength, maxLength,
 * pattern — and composes them into Zod refinements at load time.
 *
 * This eliminates boilerplate validation rules in rules.json. A field that
 * previously needed 3 separate rules (noBlank, doubleSpace, chars) now declares
 * them directly on the property:
 *
 *   "firstName": {
 *     "type": "string",
 *     "title": "First Name",
 *     "maxLength": 100,
 *     "noBlank": true,
 *     "noDoubleSpace": true,
 *     "charset": "nameChars"
 *   }
 */

export interface KeywordDef {
  pattern: RegExp;
  message: string;
}

const BOOLEAN_KEYWORDS: Record<string, KeywordDef> = {
  noBlank: {
    pattern: /^\S[\s\S]*$/,
    message: 'Must not contain blank spaces',
  },
  noDoubleSpace: {
    pattern: /^(?!.*  )[\s\S]*$/,
    message: 'Must not contain double space',
  },
  digitsOnly: {
    pattern: /^[0-9]+$/,
    message: 'Must be only digits',
  },
};

const VALID_CHARS_PRESETS: Record<string, KeywordDef> = {
  nameChars: {
    pattern: /^[a-zA-Z0-9 @()\-/*"':;_#\-+]+$/,
    message: 'Invalid {title}',
  },
  alphanumeric: {
    pattern: /^[a-zA-Z0-9]+$/,
    message: 'Must be alphanumeric only',
  },
  alphanumericSpace: {
    pattern: /^[a-zA-Z0-9 ]*$/,
    message: 'Must be alphanumeric only',
  },
  alphanumericDot: {
    pattern: /^[a-zA-Z0-9 .,]*$/,
    message: 'Must be alphanumeric only',
  },
  address: {
    pattern: /^[a-zA-Z0-9 .,'\-]*$/,
    message: 'Invalid address',
  },
  numericPhone: {
    pattern: /^[0-9\-]+$/,
    message: 'Must be only digits',
  },
  ticker: {
    pattern: /^[A-Z]{1,10}([-\.][A-Z]{1,3})?$/,
    message: 'Invalid ticker',
  },
};

export function getBooleanKeyword(name: string): KeywordDef | undefined {
  return BOOLEAN_KEYWORDS[name];
}

export function getValidCharsPreset(presetId: string): KeywordDef | undefined {
  return VALID_CHARS_PRESETS[presetId];
}

export function resolveMessage(message: string, title: string): string {
  return message.replace(/\{title\}/g, title);
}
